// src/main/shell/tree-search/TreeSearch.ts
// A small framework-agnostic controller that wires together a search input, the
// syntax-highlight overlay, and the TreeAutocomplete dropdown so a host just
// attaches it to its search-wrap DOM. Generic over the item type `T` and host
// context `C`.
//
// The search-wrap is expected to already contain (matching the symbol grid's
// toolbar structure):
//   - input.vg-search-input      — the text field
//   - div.vg-search-highlight    — the colored-token overlay
//
// The overlay-token rendering, input/keydown/select handlers, and last-'/'-to-
// cursor completion replacement are copied verbatim from SymbolGrid so both the
// vanilla grid and a Lit host get identical behavior.
import { formatSearchInput } from './engine';
import { TreeAutocomplete } from './Autocomplete';
export class TreeSearch {
    constructor(searchWrap, options) {
        this.options = options;
        const input = searchWrap.querySelector('input.vg-search-input');
        const highlight = searchWrap.querySelector('div.vg-search-highlight');
        if (!input) {
            throw new Error('TreeSearch: search-wrap must contain an input.vg-search-input');
        }
        if (!highlight) {
            throw new Error('TreeSearch: search-wrap must contain a div.vg-search-highlight');
        }
        this.input = input;
        this.highlight = highlight;
        // --- Initialize autocomplete ---
        this.autocomplete = new TreeAutocomplete(searchWrap, {
            commands: options.commands,
            ctx: options.ctx,
            iterate: options.iterate,
            valueForCommand: options.valueForCommand,
            textMatch: options.textMatch,
            onSelect: (completion) => {
                // Replace from last '/' to end with the completion
                const val = this.input.value;
                const lastSlash = val.lastIndexOf('/');
                const before = lastSlash >= 0 ? val.slice(0, lastSlash) : val;
                this.input.value = before + completion;
                this.updateHighlight();
                // Notify the host of the change
                this.options.onChange(this.input.value);
                // Re-focus and put cursor at end
                this.input.focus();
                this.input.setSelectionRange(this.input.value.length, this.input.value.length);
            },
            onClose: () => {
                // Nothing extra needed
            },
        });
        // --- Wire events ---
        this.onInput = () => {
            this.updateHighlight();
            this.updateAutocomplete();
            this.options.onChange(this.input.value);
        };
        this.onFocus = () => {
            // Show autocomplete if there's a partial command
            this.updateAutocomplete();
        };
        this.onBlur = () => {
            // Slight delay so mousedown on dropdown item fires first
            setTimeout(() => {
                this.autocomplete.hide();
            }, 150);
        };
        this.onKeyDown = (e) => {
            if (this.autocomplete.isOpen()) {
                if (['ArrowDown', 'ArrowUp', 'Tab', 'Enter', 'Escape'].includes(e.key)) {
                    this.autocomplete.handleKeyDown(e);
                    return;
                }
            }
        };
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('focus', this.onFocus);
        this.input.addEventListener('blur', this.onBlur);
        this.input.addEventListener('keydown', this.onKeyDown);
    }
    /** Re-render the colored-token overlay from the current input value. */
    updateHighlight() {
        const value = this.input.value;
        if (!value) {
            this.highlight.innerHTML = '';
            return;
        }
        const tokens = formatSearchInput(value, this.options.commands);
        this.highlight.innerHTML = tokens
            .map((tok) => {
            const cls = `vg-token-${tok.type}`;
            // Escape HTML
            const escaped = tok.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            return `<span class="${cls}">${escaped}</span>`;
        })
            .join('');
    }
    /** Drive the autocomplete dropdown from the current input value + cursor. */
    updateAutocomplete() {
        const value = this.input.value;
        const cursorPos = this.input.selectionStart ?? value.length;
        // Only show autocomplete when there's a '/' in the input
        if (!value.includes('/')) {
            this.autocomplete.hide();
            return;
        }
        const rect = this.input.getBoundingClientRect();
        this.autocomplete.update(value, cursorPos, rect);
    }
    /** Programmatically set the input value (refreshes highlight; no onChange). */
    setValue(v) {
        this.input.value = v;
        this.updateHighlight();
    }
    destroy() {
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('blur', this.onBlur);
        this.input.removeEventListener('keydown', this.onKeyDown);
        this.autocomplete.destroy();
    }
}
//# sourceMappingURL=TreeSearch.js.map